import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useResetPasswordMutation } from '@@/services/auth';
import StandardInput from '@@/components/Inputs/StandardInput';
import { Lock, Mail, RefreshCw, ArrowLeft } from 'lucide-react';

const schema = yup
  .object({
    email: yup.string().email().required('Veuillez entrer votre email'),
  })
  .required();

const ResetPasswordPage: React.FC = () => {
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ email: string }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const [resetPassword, { isSuccess }] = useResetPasswordMutation();

  const onSubmit = useCallback(
    (data: { email: string }) => {
      resetPassword(data);
    },
    [resetPassword],
  );

  useEffect(() => {
    if (isSuccess) {
      reset();
      toast.success(
        'Un email de réinitialisation de mot de passe vous a été envoyé',
      );
    }
  }, [isSuccess, reset]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100">
      <div className="py-8 flex items-center justify-center">
        <div className="w-full max-w-md px-4">
          {/* Header compact */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-lg mb-3">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-1">
              Mot de passe oublié ?
            </h1>
            <p className="text-neutral-600 text-sm">
              Récupérez l'accès à votre compte
            </p>
          </div>

          {/* Formulaire */}
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
            <p className="text-center text-neutral-600 text-sm mb-6 leading-relaxed">
              Pour réinitialiser votre mot de passe, indiquez votre email
              ci-dessous
            </p>

            <div className="space-y-4">
              {/* Email */}
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <Mail size={16} className="text-primary-600" />
                  <label className="text-sm font-medium text-neutral-700">
                    Email *
                  </label>
                </div>
                <StandardInput
                  register={register}
                  id="email"
                  label=""
                  placeholder="votre@email.com"
                  error={errors?.email}
                  type="email"
                />
              </div>

              {/* Bouton d'envoi */}
              <button
                type="button"
                className={`
                  w-full py-2.5 px-4 rounded-lg font-semibold text-white text-sm
                  transition-all duration-200 
                  ${
                    isSubmitting
                      ? 'bg-neutral-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 shadow-md hover:shadow-lg'
                  }
                `}
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Envoi...</span>
                  </div>
                ) : (
                  <span>Envoyer le lien</span>
                )}
              </button>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-neutral-200 text-center">
              <Link
                to="/signin"
                className="inline-flex items-center gap-2 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <ArrowLeft size={14} />
                Retour à la connexion
              </Link>
            </div>
          </div>

          {/* Information supplémentaire */}
          <div className="mt-4 text-center">
            <p className="text-xs text-neutral-500">
              Vous ne recevez pas l'email ? Vérifiez vos spams ou{' '}
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                renvoyez le lien
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
